'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

// URL 파라미터 타입 정의
type ParamValue = string | number | null;
type ParamsObject = Record<string, ParamValue>;

export function useShallowRouting() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // 현재 search params를 객체로 변환하는 유틸리티 함수
  const getCurrentParams = useCallback((): ParamsObject => {
    const params: ParamsObject = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [searchParams]);

  // 단일 파라미터 업데이트
  const setParam = useCallback(
    (key: string, value: ParamValue) => {
      const params = new URLSearchParams(searchParams);

      if (value === null || value === '') {
        params.delete(key);
      } else {
        params.set(key, String(value));
      }

      router.push(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams],
  );

  // 여러 파라미터 동시 업데이트
  const setParams = useCallback(
    (newParams: ParamsObject) => {
      const params = new URLSearchParams(searchParams);

      Object.entries(newParams).forEach(([key, value]) => {
        if (value === null || value === '') {
          params.delete(key);
        } else {
          params.set(key, String(value));
        }
      });

      router.push(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams],
  );

  // 특정 파라미터 삭제
  const deleteParam = useCallback(
    (key: string) => {
      const params = new URLSearchParams(searchParams);
      params.delete(key);
      router.push(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams],
  );

  // 모든 파라미터 삭제
  const clearParams = useCallback(() => {
    router.replace(pathname, { scroll: false });
  }, [router]);

  // 특정 파라미터 값 가져오기
  const getParam = useCallback(
    (key: string) => {
      return searchParams.get(key);
    },
    [searchParams],
  );

  return {
    params: getCurrentParams(),
    setParam,
    setParams,
    deleteParam,
    clearParams,
    getParam,
  };
}
